import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import { useEvent } from 'context/events/Event';
import masks from 'helpers/masks';
import regex from 'helpers/regex';
import React, { useState, useEffect } from 'react'
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import addressApi from 'api/requests/address';
import colors from 'theme/colors';
import fieldMessages from 'texts/messages/field';
import Switch from 'components/_UI/Switch';
import { MapPin, CheckCircle } from 'react-feather';
import breakpoints from 'theme/breakpoints';

export default function AddressInfo({ trigger, edit }) {
    const { event, setEvent } = useEvent();

    const [address, setAddress] = useState(null);
    const [addressLoading, setAddressLoading] = useState(false);
    const [cepFilled, setCepFilled] = useState(false);
    const [cepValue, setCepValue] = useState(edit ? edit?.address?.street?.postalcode : '');
    const [numberValue, setNumberValue] = useState('');
    const [nameValue, setNameValue] = useState('');
    const [hideAddress, setHideAddress] = useState(false);
    const [complementValue, setComplementValue] = useState('');
    const [withoutNumber, setWithoutNumber] = useState(false);
    const [errors, setErrors] = useState(false);
    const [noAddress, setNoAddress] = useState(false);

    useEffect(() => {
        if (edit && !trigger) {
            setCepFilled(true);
            setHideAddress(!!edit?.hide_address)

            if (edit?.address?.number) {
                setAddress({ ...edit?.address })
                setNumberValue(edit?.address?.number)
                showAddressFields(edit?.address?.street?.postalcode, true)
            }

            if (edit?.address?.complement) {
                setComplementValue(edit?.address?.complement)
                setAddress(edit?.address)
                setEvent({
                    ...edit,
                    address: {
                        ...edit.address,
                        complement: edit.address.complement
                    }
                })
            }

            if (edit?.address?.name) {
                setWithoutNumber(true)
                setNameValue(edit?.address?.name)
                setAddress(edit?.address)
                setEvent({
                    ...edit,
                    address: {
                        ...edit?.address,
                        name: edit?.address?.name
                    }
                })
                showAddressFields(edit?.address?.street?.postalcode)
            }

            return
        }

        if (trigger && !cepFilled && !noAddress) {
            setErrors({ ...errors, cep: 'CEP inválido' })
            return
        }

        if (
            trigger
            && cepFilled
            && event?.address?.number
            && event?.address?.name
        ) {
            setErrors({ ...errors, number: fieldMessages.required('número') })
        }


    }, [trigger])

    async function getAddress(payload, firstTimeEdit) {
        if (firstTimeEdit) {
            return
        }

        setAddressLoading(true)
        try {
            const { data } = await addressApi.getByPostalCode(payload);

            setAddress(data)
            setEvent({ ...event, address: data })
        } catch (error) {
            setErrors({ ...errors, cep: 'CEP inválido' })
            setAddress(false)
        } finally {
            setAddressLoading(false)
        }
    }

    function validateCEP(cep) {
        return regex.cep.test(cep);
    }

    function showAddressFields(value, firstTimeEdit = false) {
        setCepValue(value)

        if (!validateCEP(value)) {
            if (trigger) {
                setErrors({ ...errors, cep: 'CEP inválido' })
            }
            return
        }

        const postalCodeNumbers = value.replace(regex.onlyNumber, '');

        if (postalCodeNumbers.length === 9) {
            setErrors({ ...errors, cep: false })
            setCepFilled(true);
            getAddress(postalCodeNumbers, firstTimeEdit)
        }
    };

    function onChangeNumber(e) {
        setNumberValue(e)
        setNameValue(null)
        setErrors({ ...errors, number: null });

        if (withoutNumber && !address?.number) {
            setEvent({
                ...event,
                address: {
                    postalcode: cepValue,
                    name: address?.street?.name
                },
            });

            return
        }

        setEvent({
            ...event,
            address: {
                postalcode: cepValue,
                number: e
            },
        });
    }

    function clearAddress() {
        const newNoAddress = !noAddress;
        setNoAddress(newNoAddress);

        if (newNoAddress) {
            setEvent({
                ...event,
                address: { notYet: true }
            });
            setCepFilled(false);
            setCepValue('');
        } else {
            setEvent({
                ...event,
                address: null
            });
        }
    }

    return (
        <Column>
            <SectionHeader>
                <SectionIcon><MapPin size={18} color={colors.occasionalPurple} /></SectionIcon>
                <Text type='bold' color={colors.nightDark}>
                    Endereço
                </Text>
            </SectionHeader>

            <Switch
                label='Ainda não tenho um endereço para o evento'
                marginBottom='14px'
                checked={noAddress}
                onChange={clearAddress}
            />

            {!noAddress && (
                <>
                    <Fieldset
                        placeholder='Digite o CEP do local'
                        layout='squired'
                        mask={masks.cep}
                        value={cepValue}
                        name="cep"
                        syncProp={true}
                        onChangeValue={(e) => showAddressFields(e)}
                    />

                    {errors?.cep && (
                        <ErrorText>{errors.cep}</ErrorText>
                    )}

                    {addressLoading && <Loader />}

                    {address && cepFilled && !addressLoading && (
                        <AddressDetails>
                            <AddressFound>
                                <CheckCircle size={14} color={colors.success} />
                                <Text name='small' color={colors.success} type='bold'>
                                    Endereço encontrado
                                </Text>
                            </AddressFound>

                            <FieldsGrid>
                                <Fieldset
                                    label='Bairro'
                                    layout='squired'
                                    value={address.street.district}
                                    disabled={'disabled'}
                                />
                                <Fieldset
                                    label='Rua'
                                    layout='squired'
                                    value={address.street.name}
                                    disabled={'disabled'}
                                />
                            </FieldsGrid>

                            <FieldsGrid>
                                <Fieldset
                                    label='Número'
                                    layout='squired'
                                    disabled={withoutNumber ? 'disabled' : ''}
                                    value={numberValue}
                                    onChangeValue={(e) => onChangeNumber(e)}
                                    refresh={withoutNumber}
                                />
                                <Fieldset
                                    label='Complemento'
                                    layout='squired'
                                    value={complementValue}
                                    syncProp={true}
                                    disabled={withoutNumber ? 'disabled' : ''}
                                    onChangeValue={(e) => {
                                        setComplementValue(e)
                                        setEvent({
                                            ...event,
                                            address: {
                                                ...event.address,
                                                complement: e
                                            }
                                        })
                                    }}
                                />
                            </FieldsGrid>

                            <Switch
                                checked={withoutNumber}
                                label='O endereço não possui número'
                                marginTop='4px'
                                onChange={() => {
                                    setWithoutNumber(!withoutNumber);
                                    setEvent({
                                        ...event,
                                        address: { ...event.address, number: null },
                                    });
                                    onChangeNumber(false)
                                }}
                            />

                            {withoutNumber && (
                                <Fieldset
                                    layout='squired'
                                    label='Nome do local'
                                    value={nameValue}
                                    onChangeValue={(e) => {
                                        setNameValue(e)
                                        setEvent({
                                            ...event,
                                            address: {
                                                ...event.address,
                                                name: e
                                            }
                                        })
                                    }}
                                />
                            )}

                            <Switch
                                checked={!!hideAddress}
                                onChange={(e) => {
                                    setEvent({
                                        ...event,
                                        hide_address: e
                                    })
                                    setHideAddress(e);
                                }}
                                label='Não mostrar o endereço na página do evento'
                                marginTop='4px'
                            />
                        </AddressDetails>
                    )}
                </>
            )}

            {errors?.number && (
                <ErrorText style={{ marginTop: 12 }}>{errors.number}</ErrorText>
            )}
        </Column>
    )
}

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const AddressDetails = styled.div`
    margin-top: 8px;
    padding: 16px;
    background: ${colors.neutralBackground};
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
`;

const AddressFound = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
`;

const FieldsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 4px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }
`;

const ErrorText = styled.p`
    color: ${colors.danger};
    font-size: 12px;
    margin-top: 4px;
    padding-left: 2px;
`;
